import React from 'react'
import Head from 'next/head'
import { useRouter } from 'next/router'
import queryString from 'query-string'
import dayjs from 'dayjs'

import { Avatar } from '@wartek-id/avatar'
import { Button } from '@wartek-id/button'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog'
import { Icon } from '@wartek-id/icon'
import { Text } from '@wartek-id/text'

import PelatihanDetailShell from '../components/PelatihanDetailShell'
import CheckmarkIcon from '../components/CheckmarkIcon'
import MaterialList from '../components/MaterialList'
import ModuleDetailsPlaceholder from '../partials/Module/ModuleDetailsPlaceholder'
import PostTestCard from '../partials/Module/PostTestCard'
import {
  getQueryLabel,
  getSerializableQueryValue,
} from '../utils/detailNavigation'
import { GATrackEvent } from 'utils/googleAnalytics'
import { sanitize } from 'utils/text'

import type { ParsedQuery } from 'query-string'
import type {
  MaterialDataResponse,
  PostTestState,
  ResponseModulData,
} from 'types/microlearning'
import type { CommonListResponse, CommonResponse } from 'types/api'

type ModuleDetailsDesktopProps = {
  content?: React.ReactNode
  displayMode?: 'page' | 'panel'
  isDrawerOpen: boolean
  isLoading: boolean
  isSuccess: boolean
  lastExpandedIds: string[]
  materialData?: CommonListResponse<MaterialDataResponse, any>
  modulePreview?: CommonResponse<ResponseModulData>
  onDialogClose: () => void
  returnView?: 'topic'
  toggleMateriExpand: (id: string) => void
  toggleShowDetail: () => void
}

type ModulePanelProps = Pick<
  ModuleDetailsDesktopProps,
  | 'content'
  | 'isLoading'
  | 'isSuccess'
  | 'lastExpandedIds'
  | 'materialData'
  | 'modulePreview'
  | 'returnView'
  | 'toggleMateriExpand'
  | 'toggleShowDetail'
>

const generatePostTestUrl = (
  modulId: string,
  modulName: string,
  topikId: string,
  topikName: string | undefined,
  postTestState: PostTestState,
  from = '',
  menuId = '',
  returnView?: 'topic'
) => {
  let baseUrl = `/pelatihan-mandiri/modul/${modulId}/post-test`

  if (postTestState === 'passed') {
    baseUrl = `${baseUrl}/passed`
  }

  return `${baseUrl}?${queryString.stringify({
    topik: topikId,
    topik_name: topikName,
    module_name: modulName,
    from,
    menuId,
    return_view: returnView,
    testPassed: postTestState === 'passed',
  })}`
}

const ModuleOverview = ({
  modulePreview,
}: {
  modulePreview: CommonResponse<ResponseModulData>
}) => (
  <div>
    <Text variant="subheading" color="subdued" className="mb-2 uppercase">
      Modul
    </Text>
    <Text variant="heading-lg" className="mb-4">
      {modulePreview.data.name}
    </Text>
    <Text
      as="div"
      className="simple-wysiwyg-content mb-6"
      dangerouslySetInnerHTML={{
        __html: sanitize(modulePreview.data.description),
      }}
    />
    {modulePreview.data.learningMaterial?.materials?.length ? (
      <div className="mb-6">
        <Text variant="heading-sm" className="mb-2">
          Daftar Materi
        </Text>
        <ul className="list-disc space-y-1 pl-5">
          {modulePreview.data.learningMaterial.materials.map((material) => (
            <li key={material.id}>{material.name}</li>
          ))}
        </ul>
      </div>
    ) : null}
    <Text variant="heading-sm" className="mb-2">
      Langkah Penyelesaian Modul
    </Text>
    <ol className="mb-6 list-decimal space-y-1 pl-5">
      <li>Belajar materi</li>
      <li>Refleksi pembelajaran</li>
      <li>Post Test</li>
    </ol>
    <div className="flex items-center gap-3 border-t border-gray-20 pt-4">
      <Avatar
        alt=""
        shape="circle"
        size="lg"
        src={modulePreview.data.reviewer?.imageUrl}
      />
      <Text variant="body-sm">
        Dikembangkan oleh {modulePreview.data.reviewer?.name}
      </Text>
    </div>
  </div>
)

const ModulePanel = ({
  content,
  isLoading,
  isSuccess,
  lastExpandedIds,
  materialData,
  modulePreview,
  returnView,
  toggleMateriExpand,
  toggleShowDetail,
}: ModulePanelProps) => {
  const { pathname, query } = useRouter()

  if (isLoading || !isSuccess || !modulePreview) {
    return <ModuleDetailsPlaceholder variant="desktop" />
  }

  if (content) {
    return <>{content}</>
  }

  const materialQuery = {
    ...query,
    modul_name: modulePreview.data.name,
    return_view: returnView,
  } as ParsedQuery
  const isPostTestActive = pathname.includes('/post-test/')
  const isPostTestHistoryActive = pathname.endsWith('/post-test-history')

  return (
    <div data-testid="module-workspace" className="space-y-5">
      <section className="rounded-lg border border-gray-20 bg-white p-6">
        <Text variant="subheading" color="subdued" className="mb-1 uppercase">
          Modul
        </Text>
        <Text variant="heading-lg" className="mb-3">
          {modulePreview.data.name}
        </Text>
        <div className="mb-4 flex items-center gap-3">
          <Avatar
            alt=""
            shape="circle"
            size="sm"
            src={modulePreview.data.reviewer?.imageUrl}
          />
          <Text variant="body-sm" color="subdued">
            Dikembangkan oleh {modulePreview.data.reviewer?.name}
          </Text>
        </div>
        <Button
          data-testid="toggle-detail"
          color="white"
          size="sm"
          onClick={toggleShowDetail}
        >
          Baca detail modul
        </Button>
      </section>

      <section className="rounded-lg border border-gray-20 bg-white p-6">
        <MaterialList
          materialList={materialData?.data ?? []}
          query={materialQuery}
          toggleMateriExpand={toggleMateriExpand}
          lastExpandedIds={lastExpandedIds}
        />
        <Text className="mb-2 mt-6" variant="heading-sm">
          Post Test
        </Text>
        <PostTestCard
          href={generatePostTestUrl(
            String(query.modul),
            modulePreview.data.name,
            String(query.topik),
            getSerializableQueryValue(query.topik_name),
            modulePreview.data.postTest?.state,
            query.from as string,
            query.menuId as string,
            returnView
          )}
          title="Kerjakan post test"
          description={
            modulePreview.data.postTest?.state === 'not_passed'
              ? 'Post test perlu diulang'
              : 'Evaluasi pemahaman modul'
          }
          isLocked={!modulePreview.data.learningMaterial?.completed}
          icon={
            <CheckmarkIcon postTestState={modulePreview.data.postTest?.state} />
          }
          isActive={isPostTestActive}
          trackStep={() =>
            GATrackEvent({
              eventName: 'learning_activity',
              eventParams: {
                event_trigger: 'click_module_activity_card',
                action_type: 'click_module_activity',
                meta: JSON.stringify({
                  topik_id: String(query.topik),
                  module_id: String(query.modul),
                  type: 'post_test',
                }),
              },
              sendDefaultParams: true,
            })
          }
        />
        {modulePreview.data.postTest?.state === 'not_passed' ? (
          <div className="mt-3">
            <PostTestCard
              href={`/pelatihan-mandiri/modul/${
                query.modul
              }/post-test-history?${queryString.stringify({
                topik: query.topik,
                topik_name: getSerializableQueryValue(query.topik_name),
                module_name: modulePreview.data.name,
                from: query.from,
                menuId: query.menuId,
                return_view: returnView,
              })}`}
              title="Hasil post test terakhir"
              description={dayjs().format('DD/MM/YYYY')}
              isLocked={!modulePreview.data.learningMaterial?.completed}
              icon={<Icon>history</Icon>}
              isActive={isPostTestHistoryActive}
              trackStep={() =>
                GATrackEvent({
                  eventName: 'learning_activity',
                  eventParams: {
                    event_trigger: 'click_module_activity_card',
                    action_type: 'click_module_activity',
                    meta: JSON.stringify({
                      topik_id: String(query.topik),
                      module_id: String(query.modul),
                      type: 'post_test',
                    }),
                  },
                  sendDefaultParams: true,
                })
              }
            />
          </div>
        ) : null}
      </section>
    </div>
  )
}

const ModuleDetailsDesktop = ({
  displayMode = 'page',
  isDrawerOpen,
  modulePreview,
  onDialogClose,
  ...panelProps
}: ModuleDetailsDesktopProps) => {
  const { query } = useRouter()
  const panel = <ModulePanel modulePreview={modulePreview} {...panelProps} />

  return (
    <>
      {displayMode === 'panel' ? (
        panel
      ) : (
        <>
          <Head>
            <title>
              Pelatihan Mandiri | Modul {modulePreview?.data?.name ?? ''}
            </title>
          </Head>
          <PelatihanDetailShell
            breadcrumbs={[
              { label: 'Pelatihan Mandiri', href: '/pelatihan-mandiri' },
              {
                label: getQueryLabel(query.topik_name, 'Topik'),
                href: `/pelatihan-mandiri/topik/${query.topik}`,
              },
              {
                label: String(modulePreview?.data?.name ?? 'Modul'),
                href: null,
              },
            ]}
          >
            {panel}
          </PelatihanDetailShell>
        </>
      )}

      {modulePreview ? (
        <Dialog
          isOpen={isDrawerOpen}
          size="sm"
          onClose={onDialogClose}
          closeOnOverlayClick
        >
          <DialogOverlay />
          <DialogContent>
            <div className="max-h-[80vh] overflow-y-auto p-6">
              <div className="mb-4 flex items-center justify-between">
                <Text variant="heading-md">Detail modul</Text>
                <button
                  data-testid="close-module-detail"
                  type="button"
                  onClick={onDialogClose}
                >
                  <Icon>close</Icon>
                </button>
              </div>
              <ModuleOverview modulePreview={modulePreview} />
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  )
}

export default ModuleDetailsDesktop
